import { Link } from "gatsby"
import React, { useContext } from "react"
import { Helmet } from "react-helmet"
import { IoWifi } from "react-icons/io5"
import { getValue, stringValue } from "../../../../../gatsby-source-harvia-pim/helpers/attribute"
import { getExternalMedia } from "../../../../../gatsby-source-harvia-pim/helpers/media"
import { ExternalMedia, Product, ProductValue } from "../../../../../gatsby-source-harvia-pim/types/plugin"
import { RockonAssociationData } from "../../../../../gatsby-source-harvia-pim/types/rockon"
import { PageContext } from "../../../context/PageContext"
import { useUserPreferences } from "../../../context/UserContext"
import useDatoMicrocopy from "../../../hooks/useDatoMicrocopy"
import useProductLandingPagePath from "../../../hooks/useProductLandingPagePath"
import { RoutePathNode } from "../../../hooks/useRouteTree"
import { findAny, slugify } from "../../../utils"
import Button from "../../atoms/Button"
import TileLayout from "../../layouts/TileLayout"
import BackgroundMedia from "../../molecules/BackgroundMedia"
import BreadCrumbs from "../../molecules/BreadCrumbs"
import ExpandingText from "../../molecules/ExpandingText"
import TabContent, { TabContentProps } from "../../molecules/TabContent"
import MediaTextBlock from "../../molecules/tileable-blocks/MediaTextBlock"
import ProductAttribute from "../../organisms/product/ProductAttribute"
import ProductFileLink from "../../organisms/product/ProductFileLink"
import ProductFilter from "../../organisms/product/ProductFilter"
import ProductMarketAreas from "../../organisms/product/ProductMarketAreas"
import { getAssocQuantities, isAvailable, textFormat } from "../../organisms/product/utils"
import ProductLeadDescription from "./ProductLeadDescription"
import ProductPageComparison from "./ProductPageComparison"
import * as styles from "./ProductPageContent.module.scss"
import ProductPageImages, { ProductPageImage } from "./ProductPageImages"
import ProductVideoEmbed from "./ProductVideoEmbed"

export type ProductLandingPageCta = Queries.ProductLandingPageCtaFragment

export type ProductAssociation = {
  sku: string
  data?: RockonAssociationData
  product?: {
    identifier: string
    name: string
    media?: ExternalMedia[]
    values?: ProductValue[]
  }
}

type ProductPageContentProps = {
  /** Product data */
  product: Product
  /** Landingpage cta:s */
  landingPages?: ProductLandingPageCta[]
  /** Using dynamic data vs built data */
  unBuiltProduct?: boolean
  /** URL path to Find Your Dealer page. No value implies not to link there. */
  findDealerPath?: string
  /** URL path to Sauna Planner page. No value implies not to link there. */
  saunaPlannerPath?: string
  /** URL path to Sauna Calculator page. No value implies not to link there. */
  saunaCalculatorPath?: string
  /** TODO Needed only for breadcrupbs. Remove when breadcrumbs can be calculated outside */
  productsPageRootPath?: string
  /** Meta title. To be removed: TODO move Helmet/SEO stuff to parent or layout or somewhere above this */
  siteTitle?: string | null
  /** TODO */
  breadcrumbBase: (RoutePathNode | null)[]
  categoryLabel: string
}

const ProductPageContent = (props: ProductPageContentProps) => {
  // console.debug("Rendering ProductPage", props)
  // React.useEffect(() => console.debug("Rendering ProductPage", props), [])

  const findProductLandingPagePath = useProductLandingPagePath()

  const userPrefs = useUserPreferences()

  const { locale, localePim } = useContext(PageContext)
  const t = useDatoMicrocopy("product", locale)

  const productData = props.product

  /**  */
  const pValueStore = new Map<string, ProductValue | undefined>()

  /** Whitelist of "product value" keys used in this component */
  const pValueKeys = [
    "product_marketing_name_short",
    "product_marketing_name_long",
    "datasheet",
    "external_link_1_videos_etc",
    "external_link_2_videos_etc",
    "external_link_3_videos_etc",
    "product_life_cycle_status",
    "product_family",
    "electrical_number_stk",
    "control_panel_type",
    "marketing_slogan",
    "room_min_volume",
    "room_max_volume",
    "item_code_harvia_finland",
    "type_code",
    "electrical_number_seg",
    "market_areas",
    "product_family_text_maaritetaan_tuoteperheelle",
    "usp_1",
    "usp_2",
    "usp_3",
    "usp_4",
    "usp_5",
    "usp_6",
    "usp_7",
    "usp_8",
    "usp_9",
  ]

  pValueKeys.forEach(pValKey => {
    pValueStore.set(pValKey, getValue(productData.values, pValKey))
  })

  function pValue(key: string): ProductValue | undefined {
    if (!pValueStore.has(key)) {
      console.warn(`Unkown product value key "${key}"`)
    }
    return pValueStore.get(key)
  }

  function pValues(keys: string[]): (ProductValue | undefined)[] {
    return keys.map(pValue)
  }

  function pValueLabel(key: string) {
    const v = pValue(key)
    if (v && v.value[0]) return v.value[0].label
    return ""
  }

  if (!props.product || !props.product.identifier) return null

  // muodostetaan muropolku
  const breadcrumbs = [
    { label: stringValue(pValue("product_marketing_name_short")), path: "" },
    ...props.breadcrumbBase,
  ]

  /** Assosioidut tuotteet. Saatavilla ssr tilassa. */
  const getAssociatedProducts = (assocKey: string) =>
    (productData.associations[assocKey] as ProductAssociation[])?.map(itm => itm.product as Product).filter(Boolean)

  const dataSheet = pValue("datasheet")?.value[0]
  const dataSheetUrl =
    dataSheet && `https://pim.harvia.com/media/download/${encodeURIComponent(encodeURIComponent(dataSheet.data))}`

  const showSaunaCalculator = !!findAny(productData.categories, [
    "02_sauna_electric_heaters",
    "02_sauna_wood_heaters",
    // "02_steamandspa_steam_generators",
  ])
  const showSaunaPlanner = !!findAny(productData.categories, ["02_sauna_measure_made_benches"])

  // USP
  const uspList = pValues(["usp_1", "usp_2", "usp_3", "usp_4", "usp_5", "usp_6", "usp_7", "usp_8", "usp_9"])

  // Downloadable files
  const mediaMauals = getExternalMedia(productData.media, "User manual")
  const mediaBrochures = getExternalMedia(productData.media, "Brochure")
  const mediaCertificates = getExternalMedia(productData.media, "Certificate")
  const mediaTechnicaImages = getExternalMedia(productData.media, "Technical image")
  const mediaTechnicaModels = [
    ...getExternalMedia(productData.media, "3D CAD file"),
    ...getExternalMedia(productData.media, "2D CAD file"),
    ...getExternalMedia(productData.media, "BIM object"),
  ]
  const safetyDataSheets = getExternalMedia(productData.media, "Safety data sheet")
  const getExternalMediaLabel = (itm: ExternalMedia) =>
    itm.labels?.find(lbl => lbl.locale === localePim)?.value || itm.name // jos omalla localella ei labelia, fallbackinä itm.name

  // Videos
  const videos = [
    stringValue(pValue("external_link_1_videos_etc")),
    stringValue(pValue("external_link_2_videos_etc")),
    stringValue(pValue("external_link_3_videos_etc")),
  ].filter(Boolean)

  // Spare parts
  const sparePartsDisabled = !!findAny(productData.categories, [
    "02_sauna_spare_parts",
    "02_infrared_spare_parts",
    "02_steamandspa_spare_parts",
  ])
  //const spareParts = !sparePartsDisabled && getAssociatedProducts("Spare_parts")

  // Cross sell
  const crossSellEnabled = !findAny(productData.categories, ["02_accessories"])

  const isDiscontinued =
    pValue("product_life_cycle_status")?.value[0].data === "s5_discontinued" || productData.enabled === false

  const productFamily = pValue("product_family")
  const electricalNumberStk = pValue("electrical_number_stk")
  const controlPanelType = pValue("control_panel_type")?.value[0]

  const pageTitle =
    stringValue(pValue("product_marketing_name_long")) ||
    stringValue(pValue("product_marketing_name_short")) ||
    productData.name // fallback nimi tuotteelle
  const description = stringValue(pValue("marketing_slogan"))

  const masterImage =
    productData.media.find(img => img.masterImage) || getExternalMedia(productData.media, "Product image")[0]

  const productImages: ProductPageImage[] = [
    ...getExternalMedia(productData.media, "Feeling image").slice(0, 4),
    ...getExternalMedia(productData.media, "Detail image"),
    masterImage,
  ]
    .filter(Boolean)
    .map(img => ({
      src: img.link,
      thumbnail: img.thumbnail,
      alt: img.name,
      fit: img.type === "Product image" ? "contain" : "cover",
    }))

  const lessLabel = t("showLess", "Show less", "common")
  const moreLabel = t("showMore", "Show more", "common")

  const roomVolumeMin = pValue("room_min_volume")
  const roomVolumeMax = pValue("room_max_volume")

  // console.debug("room volume in ProductPageContent", roomVolumeMin, roomVolumeMax)

  // whether to show "Products you may also be interested in" heading or not
  // used in Tabs section
  const showAssocHeading =
    (crossSellEnabled && !!productData.associations?.cross_sell?.length) ||
    //!!productData.associations?.accessories?.length ||
    !!productData.associations?.SUBSTITUTION?.length ||
    !!productData.associations?.UPSELL?.length ||
    !!productData.associations?.compatible_items?.length

  const showFilesTab =
    !!dataSheet ||
    !!mediaMauals.length ||
    !!mediaBrochures.length ||
    !!mediaCertificates.length ||
    !!safetyDataSheets.length ||
    !!mediaTechnicaImages.length

  return (
    <article itemScope itemType="https://schema.org/Product">
      <Helmet titleTemplate={`%s | ${props.categoryLabel} | ${props.siteTitle || "Harvia"}`}>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />

        <meta name="og:title" content={pageTitle} />
        <meta name="og:type" content="product" />
        <meta name="og:image" content={masterImage?.link} />
        <meta name="og:description" content={description} />
      </Helmet>

      <div className={styles.topContainer}>
        {/* 
          
          Images 
          
          */}

        <div className={styles.images}>
          <div className={styles.stickyImages}>
            <ProductPageImages images={productImages} />
          </div>
        </div>

        {/* 
          
          Basic product info 
          
          */}

        <section className={styles.info + " layout-container"}>
          <header>
            <BreadCrumbs nodes={breadcrumbs} />

            {isAvailable(productData, userPrefs.localeArea) === false && (
              <p className={styles.unavailable}>
                {/* {userPrefs.country.flag || ""} {userPrefs.country.name?.common} —{" "} */}
                {t("infoProductUnavailable", "Product is currently unavailable.")}
              </p>
            )}

            {/* {props.unBuiltProduct && (
                <p className={styles.unavailable}>{t("infoProductUnavailable", "Product is currently unavailable.")}</p>
              )} */}

            <p style={{ marginBottom: 0 }} itemProp="category">
              {[stringValue(getValue(productData.values, "product_group_2")), stringValue(productFamily)]
                .filter(Boolean)
                .join(" | ")}
            </p>
            <h1 style={{ marginTop: 0 }} itemProp="name">
              {textFormat(pageTitle)}
            </h1>
          </header>

          <div style={{ paddingBottom: "1em" }} itemProp="description">
            <ExpandingText
              text={getValue(productData.values, "marketing_text_long")?.value?.[0]?.["text"]}
              lessLabel={lessLabel}
              moreLabel={moreLabel}
              backgroundColor="#eae8e0" // must match parent bg or no fade out effect when text not expanded!! (TODO figure out a way to remove this coupling)
            />
          </div>
          <hr />

          <dl>
            <ProductLeadDescription term={pValueLabel("item_code_harvia_finland")}>
              <ProductAttribute list={pValue("item_code_harvia_finland")?.value} />
            </ProductLeadDescription>

            <ProductLeadDescription term={pValueLabel("type_code")}>
              <ProductAttribute list={pValue("type_code")?.value} />
            </ProductLeadDescription>

            <ProductLeadDescription term={pValueLabel("electrical_number_stk")}>
              {electricalNumberStk && (
                <a
                  href={`https://www.sahkonumerot.fi/${stringValue(electricalNumberStk)}`}
                  target="_blank"
                  rel="noopener noreferrer">
                  {stringValue(electricalNumberStk)}
                </a>
              )}
            </ProductLeadDescription>
            {pValue("electrical_number_seg")?.value && (
              <ProductLeadDescription term={pValueLabel("electrical_number_seg")}>
                <ProductAttribute list={pValue("electrical_number_seg")?.value} />
              </ProductLeadDescription>
            )}
            <ProductLeadDescription term={t("infoCertificates", "Certificates")}>
              {[
                productData.categories.includes("certificates_ce") && <b>CE&nbsp;</b>,
                productData.categories.includes("certificates_eac") && <b>EAC&nbsp;</b>,
                productData.categories.includes("certificates_cULus") && <b>UL&nbsp;</b>,
                productData.categories.includes("certificates_cETLus") && <b>ETL&nbsp;</b>,
                productData.categories.includes("certificates_BIMSCh2") && <b>BIMSCh2&nbsp;</b>,
                productData.categories.includes("certificates_BImSchV_2_listing") && <b>BImSchV&nbsp;2&nbsp;</b>,
                productData.categories.includes("certificates_BImSchV_1") && <b>BImSchV&nbsp;1&nbsp;</b>,
                productData.categories.includes("certificates_Avainlippu") && <b>Avainlippu&nbsp;</b>,
              ]}
            </ProductLeadDescription>
            <ProductLeadDescription term={pValueLabel("market_areas")}>
              {pValue("market_areas") && <ProductMarketAreas product={productData} />}
            </ProductLeadDescription>

            {isDiscontinued && (
              <ProductLeadDescription term={pValueLabel("product_life_cycle_status")}>
                {t("labelDiscontinued", "Discontinued", "productFinder")}
              </ProductLeadDescription>
            )}

            {roomVolumeMin && roomVolumeMax ? (
              <ProductLeadDescription term={t("infoRoomVolume", "Room volume")}>
                <ProductAttribute list={roomVolumeMin.value} /> - <ProductAttribute list={roomVolumeMax.value} />
              </ProductLeadDescription>
            ) : (
              <div style={{ display: "inline-block", minWidth: "50%" }}>&nbsp;</div>
            )}

            {controlPanelType?.data === "xenio_wifi" && (
              <IoWifi style={{ fontSize: "2em" }} title={controlPanelType.data} />
            )}
          </dl>

          <hr />

          <div className={styles.leadLinks}>
            {!showSaunaPlanner && props.findDealerPath && (
              <Link to={props.findDealerPath}>
                <Button label={t("linkFindNearestDealers", "Find your nearest dealer")} tabIndex={-1} />
              </Link>
            )}
            {showSaunaCalculator && props.saunaCalculatorPath && (
              <Link to={props.saunaCalculatorPath}>
                <Button label={t("linkCalculateSaunaVolume", "Calculate your sauna volume")} tabIndex={-1} />
              </Link>
            )}
            {showSaunaPlanner && props.saunaPlannerPath && (
              <Link to={props.saunaPlannerPath}>
                <Button label={t("linkSaunaPlanner", "Sauna Planner")} tabIndex={-1} />
              </Link>
            )}
            {dataSheet && (
              <a href={dataSheetUrl} download>
                <Button label={t("filesDownloadDatasheet", "Download datasheet")} tabIndex={-1} />
              </a>
            )}
            {!!mediaMauals?.length &&
              (mediaMauals.length === 1 ? (
                <a href={mediaMauals[0].link} download>
                  <Button
                    label={t("filesDownloadUserManual", "Download user manual")}
                    tabIndex={-1}
                    priority="primary"
                  />
                </a>
              ) : (
                <a href={`#${slugify(`tab ${t("tabFiles", "Files")}`)}`}>
                  <Button
                    label={t("filesDownloadUserManual", "Download user manual")}
                    tabIndex={-1}
                    priority="primary"
                  />
                </a>
              ))}
          </div>
        </section>
      </div>

      {/* 
        
        TABS 
        
        */}

      <section>
        <TabContent
          tabs={
            [
              !!uspList.length && {
                title: t("tabInformation", "Information"),
                slug: slugify(`tab ${t("tabInformation", "Information")}`),
                content: (
                  <div className={"layout-container"}>
                    <TileLayout columns={2}>
                      <div style={{ paddingRight: "2em" }}>
                        <h2>
                          <ProductAttribute list={pValue("marketing_slogan")?.value} />
                        </h2>
                        <ul className={styles.uspList}>
                          {uspList.map(itm =>
                            itm ? (
                              <li key={itm.name}>
                                <ProductAttribute list={itm.value} />
                              </li>
                            ) : null
                          )}
                        </ul>
                      </div>
                      {props.landingPages?.map(data => {
                        //const { path, data } = lander
                        const path = findProductLandingPagePath(data.originalId)

                        if (!path) return null

                        return (
                          <MediaTextBlock
                            key={data.originalId}
                            layout="Tile"
                            contentBackground="SemiTransparent"
                            media={<BackgroundMedia {...data.ctaImage} />}
                            content={
                              <>
                                <h3>{data.ctaTitle}</h3>
                                <p>{data.ctaText}</p>
                                <Link to={path}>
                                  <Button label={data.ctaLinkText} tabIndex={-1} />
                                </Link>
                              </>
                            }
                          />
                        )
                      })}
                    </TileLayout>
                  </div>
                ),
              },
              {
                title: t("tabTechnical", "Technical"),
                slug: slugify(`tab ${t("tabTechnical", "Technical")}`),
                content: (
                  <div className={"layout-container"}>
                    <h2>{t("tabTechnical", "Technical")}</h2>
                    <ProductPageComparison product={productData} />
                  </div>
                ),
              },

              productFamily && {
                title: t("tabProductFamily", "Product family"),
                slug: slugify(`tab ${t("tabProductFamily", "Product family")}`),
                content: (
                  <div className={"layout-container"}>
                    <h2>{stringValue(productFamily)}</h2>
                    <p>
                      <ProductAttribute list={pValue("product_family_text_maaritetaan_tuoteperheelle")?.value} />
                    </p>

                    {productFamily?.value[0] && (
                      <ProductFilter
                        title={t("productFamilyAllItemsTitle", "All items in product family")}
                        query={`product_family:${productFamily.value[0].data}`}
                        pageSize={24}
                      />
                    )}
                  </div>
                ),
              },
              !!videos.length && {
                title: t("tabVideos", "Videos"),
                slug: slugify(`tab ${t("tabVideos", "Videos")}`),
                content: (
                  <div className={"layout-container"} style={{ paddingBottom: "1em" }}>
                    <h2>{t("tabVideos", "Videos")}</h2>
                    <TileLayout columns={2}>
                      {videos.map(url => (
                        <ProductVideoEmbed key={url} url={url} />
                      ))}
                    </TileLayout>
                  </div>
                ),
              },

              showFilesTab && {
                title: t("tabFiles", "Files"),
                slug: slugify(`tab ${t("tabFiles", "Files")}`),
                content: (
                  <div className={"layout-container"}>
                    {dataSheet && (
                      <>
                        <h2>{dataSheet.label}</h2>
                        <a href={dataSheetUrl} download>
                          <Button label={t("filesDownloadDatasheet", "Download datasheet")} tabIndex={-1} />
                        </a>
                      </>
                    )}
                    {!!mediaMauals.length && (
                      <>
                        <h2>{t("filesManuals", "User manuals")}</h2>
                        {mediaMauals.map(itm => {
                          //console.log(itm)
                          return (
                            <ProductFileLink
                              key={itm.link}
                              title={getExternalMediaLabel(itm)}
                              thumbnail={itm.thumbnail}
                              link={itm.link}>
                              {itm.language}
                            </ProductFileLink>
                          )
                        })}
                      </>
                    )}
                    {!!mediaBrochures.length && (
                      <>
                        <h2>{t("filesBrochures", "Brochures")}</h2>
                        {mediaBrochures.map(itm => {
                          //console.log(itm)
                          return (
                            <ProductFileLink
                              key={itm.link}
                              title={getExternalMediaLabel(itm)}
                              thumbnail={itm.thumbnail}
                              link={itm.link}>
                              {itm.language}
                            </ProductFileLink>
                          )
                        })}
                      </>
                    )}
                    {!!mediaCertificates.length && (
                      <>
                        <h2>{t("filesCertificates", "Certificates")}</h2>
                        {mediaCertificates.map(itm => (
                          <ProductFileLink
                            key={itm.link}
                            title={getExternalMediaLabel(itm)}
                            thumbnail={itm.thumbnail}
                            link={itm.link}>
                            {itm.language}
                          </ProductFileLink>
                        ))}
                      </>
                    )}
                    {!!mediaTechnicaImages.length && (
                      <>
                        <h2>{t("filesTechImages", "Technical images")}</h2>
                        {mediaTechnicaImages.map(itm => (
                          <ProductFileLink
                            key={itm.link}
                            title={getExternalMediaLabel(itm)}
                            thumbnail={itm.thumbnail}
                            link={itm.link}>
                            {itm.language}
                          </ProductFileLink>
                        ))}
                      </>
                    )}
                    {!!mediaTechnicaModels.length && (
                      <>
                        <h2>{t("filesTechModels", "Technical models")}</h2>
                        {mediaTechnicaModels.map(itm => (
                          <ProductFileLink
                            key={itm.link}
                            title={getExternalMediaLabel(itm)}
                            thumbnail={itm.thumbnail}
                            link={itm.link}>
                            {itm.type}
                          </ProductFileLink>
                        ))}
                      </>
                    )}
                    {!!safetyDataSheets.length && (
                      <>
                        <h2>{t("filesSafetyDataSheets", "Safety data sheet")}</h2>
                        {safetyDataSheets.map(itm => (
                          <ProductFileLink
                            key={itm.link}
                            title={getExternalMediaLabel(itm)}
                            thumbnail={itm.thumbnail}
                            link={itm.link}>
                            {itm.language}
                          </ProductFileLink>
                        ))}
                      </>
                    )}
                  </div>
                ),
              },

              !!productData.associations.accessories?.length && {
                title: t("tabAccessories", "Accessories"),
                slug: slugify(`tab ${t("tabAccessories", "Accessories")}`),
                content: (
                  <div className={"layout-container"}>
                    <ProductFilter
                      category={t("assocAccessoriesCategory", "Sauna accessories")}
                      title={t("assocAccessoriesTitle", "Add a finishing touch for your sauna")}
                      query={`accessories:${productData.identifier}`}
                      products={getAssociatedProducts("accessories")}
                      quantities={getAssocQuantities(productData.associations.accessories)}
                      pageSize={16}
                    />
                  </div>
                ),
              },

              !sparePartsDisabled &&
                !!productData.associations.Spare_parts?.length && {
                  title: t("tabSpareParts", "Spare parts"),
                  slug: slugify(`tab ${t("tabSpareParts", "Spare parts")}`),
                  content: (
                    <div className={"layout-container"}>
                      <ProductFilter
                        category={
                          (t("assocSparePartsCategory") || stringValue(pValue("product_marketing_name_short"))) + ":"
                        }
                        title={t("assocSparePartsTitle", "Spare parts")}
                        query={`Spare_parts:${productData.identifier}`}
                        pageSize={16}
                        products={getAssociatedProducts("Spare_parts")}
                        quantities={getAssocQuantities(productData.associations.Spare_parts)}
                      />
                    </div>
                  ),
                },

              /* {
                  title: "(dev)",
                  content: <ProductDataDebug product={productData} />,
                }, */
            ].filter(Boolean) as TabContentProps["tabs"]
          }
        />
      </section>

      {/* 
        
        VÄLIOTSIKKO 
        
        */}

      {showAssocHeading && (
        <div className={styles.associationsHeading}>
          <h2>{t("assocHeading", "Products you may also be interested in")}</h2>
        </div>
      )}

      {/* 
        
        ASSOSIAATIOT 
        
        */}

      <section className="layout-container">
        <ProductFilter
          hideIfEmpty
          category={t("assocSubstitutionCategory", "Product substitution")}
          title={t("assocSubstitutionTitle", "You may also be interested in")}
          query={`SUBSTITUTION:${productData.identifier}`}
          lazyLoading
          products={getAssociatedProducts("SUBSTITUTION")}
          pageSize={100}
        />

        <ProductFilter
          hideIfEmpty
          category={t("assocUpsellCategory", "Similar products")}
          title={t("assocUpsellTitle", "You may also be interested in")}
          query={`UPSELL:${productData.identifier}`}
          lazyLoading
          products={getAssociatedProducts("UPSELL")}
          pageSize={100}
        />

        <ProductFilter
          hideIfEmpty
          category={t("assocCompatibleItemsCategory", "Compatible products")}
          title={t("assocCompatibleItemsTitle", "You may also be interested in")}
          query={`compatible_items:${productData.identifier}`}
          lazyLoading
          products={getAssociatedProducts("compatible_items")}
          pageSize={100}
        />

        {crossSellEnabled && (
          <ProductFilter
            hideIfEmpty
            category={t("assocCrossSellCategory", "Extra equipment")}
            title={t("assocCrossSellTitle", "Complete the Harvia sauna experience")}
            query={`cross_sell:${productData.identifier}`}
            lazyLoading
            products={getAssociatedProducts("cross_sell")}
            pageSize={100}
          />
        )}
      </section>
    </article>
  )
}

export default ProductPageContent
